import store from '@/store';
import { userService } from '@/_services/user.service';
import { clearAuthToken, getAuthToken, hasAuthToken, setAuthToken } from './authCredentials';
import { extractUserProfile } from './authProfile';
import {
  appPerfMark,
  appPerfMeasure,
  appPerfMarkMyProfileDone
} from '@/utils/appBootstrapPerfLog.js';

/** @typedef {'unknown' | 'authenticated' | 'anonymous'} AuthSessionStatus */

/** @type {AuthSessionStatus} */
let sessionStatus = 'unknown';

let bootstrapPromise = null;

function resetBootstrapCache() {
  bootstrapPromise = null;
  sessionStatus = 'unknown';
}

function applyAuthenticatedProfile(profile) {
  appPerfMark('myprofile:dispatch:start');
  store.dispatch('user/setCurrentUser', profile);
  appPerfMeasure('myprofile:dispatch', 'myprofile:dispatch:start');
  store.commit('user/isAuthTokenExpired', false);
  sessionStatus = 'authenticated';
}

function applyAnonymousSession() {
  store.dispatch('user/logout');
  store.commit('user/isAuthTokenExpired', true);
  sessionStatus = 'anonymous';
}

const STORE_REHYDRATE_WAIT_MS = 5000;

/**
 * IndexedDB rehydrate 완료를 기다리되, 상한 시간 후에는 bootstrap을 계속한다.
 * rehydrate가 먼저 끝나면 timeout 타이머를 취소해 늦은 허위 timeout 로그를 방지한다.
 * @param {Promise<void>} storeRehydrated
 */
export async function waitForStoreRehydrate(storeRehydrated) {
  appPerfMark('storeRehydrate:wait:start');
  let timeoutId = null;

  const timeoutPromise = new Promise((resolve) => {
    timeoutId = setTimeout(() => {
      timeoutId = null;
      console.warn('[authSession] store rehydrate wait timed out; continuing bootstrap');
      appPerfMark('storeRehydrate:wait:timeout');
      resolve();
    }, STORE_REHYDRATE_WAIT_MS);
  });

  const rehydratePromise = storeRehydrated.finally(() => {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  });

  try {
    await Promise.race([rehydratePromise, timeoutPromise]);
  } catch (error) {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }
    throw error;
  }

  appPerfMeasure('storeRehydrate:wait', 'storeRehydrate:wait:start');
}

/**
 * 앱 부팅 — IndexedDB rehydrate 후 로그인 API(myprofile) 응답까지 대기.
 * @param {Promise<void>} [storeRehydrated]
 */
export async function initAppAuth(storeRehydrated) {
  if (storeRehydrated) {
    await waitForStoreRehydrate(storeRehydrated);
  }
  return bootstrapSession();
}

/**
 * 토큰이 있으면 /user/myprofile 로 세션(프로필) 수립. 앱·라우터 공통 진입점.
 */
export async function bootstrapSession() {
  if (bootstrapPromise) {
    return bootstrapPromise;
  }

  bootstrapPromise = (async () => {
    appPerfMark('bootstrapSession:start', { hasToken: hasAuthToken() });
    if (!hasAuthToken()) {
      applyAnonymousSession();
      appPerfMeasure('bootstrapSession:anonymous', 'bootstrapSession:start');
      return { authenticated: false };
    }

    try {
      appPerfMark('myprofile:http:start');
      const response = await userService.getMyProfile();
      appPerfMeasure('myprofile:http', 'myprofile:http:start');

      appPerfMark('myprofile:extract:start');
      const profile = extractUserProfile(response);
      appPerfMeasure('myprofile:extract', 'myprofile:extract:start', {
        ok: !!profile,
        userId: profile?.userId
      });
      if (!profile) {
        clearAuthToken();
        applyAnonymousSession();
        return { authenticated: false };
      }

      appPerfMark('myprofile:store:start');
      applyAuthenticatedProfile(profile);
      appPerfMarkMyProfileDone({
        userId: profile.userId,
        role: profile.userRole
      });
      appPerfMeasure('myprofile:store', 'myprofile:store:start');
      appPerfMeasure('bootstrapSession:authenticated', 'bootstrapSession:start');
      return { authenticated: true, profile };
    } catch (error) {
      console.warn('[authSession] bootstrap failed:', error);
      appPerfMeasure('bootstrapSession:error', 'bootstrapSession:start', error?.message);
      clearAuthToken();
      applyAnonymousSession();
      return { authenticated: false };
    }
  })();

  return bootstrapPromise;
}

/**
 * 라우트 가드용 — 토큰·프로필 일치 여부 보장 (프로필 없으면 bootstrap 재시도)
 */
export async function ensureSession() {
  if (!hasAuthToken()) {
    if (sessionStatus !== 'anonymous') {
      applyAnonymousSession();
    }
    return false;
  }

  if (store.getters['user/isLogined']) {
    sessionStatus = 'authenticated';
    return true;
  }

  resetBootstrapCache();
  const result = await bootstrapSession();
  return result.authenticated;
}

/**
 * 로그인 — 자격 증명 + 프로필을 한 경로로 처리
 */
export async function login(credentials) {
  resetBootstrapCache();
  const ok = await store.dispatch('user/login', credentials);
  if (ok && hasAuthToken()) {
    sessionStatus = 'authenticated';
    return true;
  }
  sessionStatus = 'anonymous';
  return false;
}

/**
 * 로그아웃 — 토큰·Vuex 세션 정리
 * @param {{ redirect?: boolean, router?: import('vue-router').default }} [options]
 */
export function logout(options = {}) {
  clearAuthToken();
  applyAnonymousSession();
  resetBootstrapCache();

  if (options.redirect && options.router) {
    options.router.push({ path: '/login' });
  }
}

export function getSessionStatus() {
  return sessionStatus;
}

export function isSessionAuthenticated() {
  return sessionStatus === 'authenticated' && store.getters['user/isLogined'];
}

/** 로그인 API 성공 시 토큰 저장 (user.service 위임용) */
export { setAuthToken, getAuthToken, clearAuthToken, hasAuthToken };
